/**
 * Analytics service — Firebase Analytics (GA4) wrapper.
 * Events worden alleen in productie gefired (emulators/dev ondersteunen geen analytics).
 */
// @ts-check
import { getAnalytics, logEvent as fbLogEvent, isSupported } from 'firebase/analytics';
import { app } from '@/lib/firebase';
import { getAnalyticsConsent } from '@/services/analyticsConsent';

/** @type {import('firebase/analytics').Analytics | null} */
let analytics = null;

export const isProduction = () => import.meta.env.PROD;

/**
 * Initialiseert analytics als de omgeving het ondersteunt.
 * Alleen in productie — dev/emulators worden overgeslagen.
 * @returns {Promise<boolean>} true als analytics beschikbaar en geïnitialiseerd is
 */
export async function initAnalytics() {
  if (analytics) return true;
  if (!isProduction()) return false;
  if (getAnalyticsConsent() !== 'accepted') return false;
  const supported = await isSupported();
  if (supported) {
    analytics = getAnalytics(app);
    return true;
  }
  return false;
}

/**
 * Fire een GA4 event. No-op als analytics niet beschikbaar is.
 * @param {string} name - Event naam
 * @param {Record<string, string | number | boolean>} [params] - Event parameters
 */
export function logEvent(name, params) {
  if (!analytics) return;
  fbLogEvent(analytics, name, params);
}

/**
 * Event: signup — nieuwe gebruiker registreert zich
 * @param {{ method: string, userId: string }} params
 */
export function logSignup(params) {
  logEvent('signup', params);
}

/**
 * Event: login — bestaande gebruiker logt in
 * @param {{ method: string, userId: string }} params
 */
export function logLogin(params) {
  logEvent('login', params);
}

/**
 * Event: task_created — anoniem
 */
export function logTaskCreated() {
  logEvent('task_created');
}

/**
 * Event: task_completed — anoniem
 */
export function logTaskCompleted() {
  logEvent('task_completed');
}

/**
 * Event: wall_hit — gebruiker loopt tegen limiet
 * @param {{ wall_type: WallType }} params
 */
export function logWallHit(params) {
  logEvent('wall_hit', params);
}

/**
 * Event: wall_upgrade_click — gebruiker klikt "interesse registreren" op de wall-modal
 * @param {{ wall_type: WallType }} params
 */
export function logWallUpgradeClick(params) {
  logEvent('wall_upgrade_click', params);
}

/**
 * Event: wall_upgrade_dismiss — gebruiker sluit de wall-modal zonder interesse te registreren
 * @param {{ wall_type: WallType }} params
 */
export function logWallUpgradeDismiss(params) {
  logEvent('wall_upgrade_dismiss', params);
}
